import { hexToRgba } from './utils/richText.js';
import { buildFixedBlockIdMap, getFixedBlockId } from './utils/blockId.js';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_ASSET } from './utils/localAssets.js';
import { getQrcodeNavDefaults } from './utils/qrcodeNav.js';
import { getQuickNavGridDefaults } from './utils/quickNav.js';
import { getImageGridDefaults } from './utils/imageGrid.js';
import { getImageCardDefaults } from './utils/imageCard.js';
import { getImageFeatureCardDefaults } from './utils/imageFeatureCard.js';
import { getImageCollageDefaults } from './utils/imageCollage.js';
import { getImageTrioDefaults } from './utils/imageTrio.js';
import { getImageMosaicDefaults } from './utils/imageMosaic.js';
import { getImageQuadDefaults } from './utils/imageQuad.js';
import { getImageHeaderDefaults } from './utils/imageHeader.js';
import { getFileHeaderDefaults } from './utils/fileHeader.js';
import { getAudioHeaderDefaults } from './utils/audioHeader.js';
import { getVideoHeaderDefaults } from './utils/videoHeader.js';
import { getContactInfoDefaults } from './utils/contactInfo.js';
import { getVideoListDefaults } from './utils/videoList.js';
import { getVideoRowDefaults } from './utils/videoRow.js';
import { getAudioLibraryDefaults } from './utils/audioLibrary.js';
import { getAudioPlayerDefaults } from './utils/audioPlayer.js';
import { getHeaderCarouselBannerDefaults, getHeaderCarouselImageDefaults } from './utils/headerCarousel.js';
import { getHeaderHeroDefaults } from './utils/headerHero.js';
import { getHeaderInfoCardDefaults } from './utils/headerInfoCard.js';
import { getHeaderInfoCardV2Defaults } from './utils/headerInfoCardV2.js';
import { getHeaderIpBrandDefaults } from './utils/headerIpBrand.js';
import { getPayImageGalleryDefaults } from './utils/payImageGallery.js';
import { getPayImageWallDefaults } from './utils/payImageWall.js';
import { getPayFileUnlockDefaults, getPayFileUnlockPreviewDefaults } from './utils/payFileUnlock.js';
import { getPayFileListDefaults, getPayFileListPreviewDefaults } from './utils/payFileList.js';
import { getPayFileCollectionDefaults, getPayFileCollectionPreviewDefaults } from './utils/payFileCollection.js';
import { getPayAudioPlayerDefaults, getPayAudioPlayerPreviewDefaults } from './utils/payAudioPlayer.js';
import { getPayAudioLibraryDefaults, getPayAudioLibraryPreviewDefaults } from './utils/payAudioLibrary.js';
import { getPayAudioPlaylistDefaults, getPayAudioPlaylistPreviewDefaults } from './utils/payAudioPlaylist.js';
import { getPayVideoPlayerDefaults, getPayVideoPlayerPreviewDefaults } from './utils/payVideoPlayer.js';
import { getPayVideoListDefaults, getPayVideoListPreviewDefaults } from './utils/payVideoList.js';
import { getPayVideoSeriesDefaults, getPayVideoSeriesPreviewDefaults } from './utils/payVideoSeries.js';

/** 菱形标题默认三色（由浅到深，靠近文字最深） */
export const DEFAULT_GEM_COLORS = ['#e0e0e0', '#999999', '#333333'];

/** 标题默认主题色 */
export const DEFAULT_TITLE_THEME_COLOR = '#205792';
export const DEFAULT_TITLE_FAQ_COLOR = DEFAULT_TITLE_THEME_COLOR;

/** 色块标题默认主题色 */
export const DEFAULT_TITLE_TAG_COLOR = DEFAULT_TITLE_THEME_COLOR;

/** 色块正文默认背景色 */
export const DEFAULT_BODY_BG_COLOR = '#ebf0f5';

/** 线框斜标正文默认双色：[边框, 斜标] */
export const DEFAULT_BODY_FRAME_ICON_COLORS = ['#333333', DEFAULT_TITLE_THEME_COLOR];

/** 支付组件默认数据（经典版，兼容旧字段名） */
export const DEFAULT_PAY_UNLOCK_CLASSIC = {
	tagText: '更多精彩',
	price: '1.00',
	originalPrice: '9.00',
	unlockCount: 328,
	buttonText: '解锁图片',
	buttonColor: '#ff4b2b',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_DATA = DEFAULT_PAY_UNLOCK_CLASSIC;

export const DEFAULT_PAY_UNLOCK_TRIAL = {
	title: '爆款短视频训练营',
	subtitle: '解锁后可观看全部内容',
	price: '68.00',
	buttonText: '解锁全部视频',
	buttonColor: '#ff3b5c',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_LIMITED = {
	tagText: '今日特价',
	price: '29.9',
	originalPrice: '69',
	countdownText: 'Agent工作流项目包',
	buttonText: '解锁全部内容',
	buttonColor: '#ff8c00',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_PACK = {
	title: '包含96份文档资料',
	subtitle: '资料大小2.35GB',
	price: '1.00',
	buttonText: '解锁全部下载权限',
	buttonColor: '#7c3aed',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_COMMUNITY = {
	title: '虚拟资源项目共创社',
	subtitle: '实战案例 · 每周更新',
	price: '39.9',
	buttonText: '立即加入',
	buttonColor: '#16a34a',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_IMAGE = {
	title: 'AI儿童绘本插画库',
	subtitle: '当前可查看 5/128 张',
	price: '29.9',
	buttonText: '解锁全部插画',
	buttonColor: '#ec4899',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_OVERSEAS = {
	title: 'AI办公效率工具包',
	subtitle: '模板·教程·持续更新',
	price: '29.9',
	currency: '￥',
	buttonText: '解锁全部内容',
	buttonColor: '#2563eb',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_CREATOR = {
	title: '已有 326 人购买',
	subtitle: '创作者累计收益￥12891.00',
	price: '36.0',
	buttonText: '立即解锁',
	buttonColor: '#ff8c00',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_MYSTERY = {
	title: '付费社群运营SOP',
	subtitle: '招募·交付·续费完整流程',
	price: '19.99',
	buttonText: '继续探索',
	buttonColor: '#7c3aed',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK = {
	title: '咸鱼资料实战营',
	subtitle: '每天更新选品与上架素材',
	statusText: '当前 2368 人在群',
	icon: 'mbm-qunzu',
	iconImage: LOCAL_ASSET.qrcodeNavDefault,
	jumpUrl: '',
	price: '39.00',
	buttonText: '申请加入',
	unlockedButtonText: '',
	buttonColor: '#2669e4',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_PLUS = {
	title: 'Agent工作流共创社',
	subtitle: '共创可复用的Agent工作流',
	tags: [
		{ text: '案例共创', textColor: '#333' },
		{ text: '持续迭代', textColor: '#666' },
	],
	icon: 'mbm-qunzu',
	iconImage: LOCAL_ASSET.qrcodeNavDefaultPlus,
	jumpUrl: '',
	price: '39.00',
	buttonText: '申请加入',
	unlockedButtonText: '',
	buttonColor: '#2461FF',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_FEATURED = {
	badgeText: '精选资料',
	title: 'AI短剧实战资料包',
	subtitle: '付费后跳转查看完整内容',
	promoTag: '限时特价',
	price: '29.90',
	assuranceText: '支付成功后可跳转',
	buttonText: '立即解锁',
	unlockedButtonText: '',
	buttonColor: '#2461FF',
	jumpUrl: '',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_COURSE = {
	badgeText: '付费社群',
	title: 'Agent工作流共创社',
	subtitle: '支付后跳转进入社群详情页',
	promoTag: '入社费用',
	price: '49.90',
	buttonText: '加入社群',
	unlockedButtonText: '',
	jumpUrl: '',
	bgImage: LOCAL_ASSET.payJumpLinkBg,
	features: [
		{ icon: LOCAL_ASSET.payJumpLinkUpdate, title: '同行交流', subtitle: '紧跟前沿' },
		{ icon: LOCAL_ASSET.payJumpLinkCase, title: '案例共创', subtitle: '学以致用' },
		{ icon: LOCAL_ASSET.payJumpLinkReplay, title: '持续更新', subtitle: '反复精进' },
	],
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_ADVANCE = {
	badgeText: '项目共创',
	title: '虚拟资源',
	titleLine2: '项目共创社',
	subtitle: '支付后跳转进入社群详情页',
	price: '49.90',
	buttonText: '加入社群',
	unlockedButtonText: '',
	jumpUrl: '',
	bgImage: LOCAL_ASSET.payJumpLinkAdvanceBg,
	features: [
		{ iconClass: 'mbm-mingzhong', title: '项目观察', subtitle: '发现真实需求' },
		{ iconClass: 'mbm-ziyuan', title: '按理拆解', subtitle: '梳理落地路径' },
		{ iconClass: 'mbm-anquan1', title: '资源互助', subtitle: '持续共享进展' },
	],
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_UNLOCK = {
	title: 'AI商品图接单工作流',
	assuranceText: '支付成功后自动跳转',
	promoTag: '限时特价',
	price: '29.90',
	buttonText: '支付并立即跳转',
	unlockedButtonText: '',
	buttonColor: '#2461FF',
	jumpUrl: '',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_VIEWALL = {
	title: '长尾词获客内容库',
	subtitle: '解锁后可永久查看',
	assuranceText: '安全支付 · 自动跳转',
	price: '29.90',
	buttonText: '立即查看',
	unlockedButtonText: '',
	buttonColor: '#2461FF',
	jumpUrl: '',
	hideOtherPrice: false,
};

export const DEFAULT_PAY_UNLOCK_JUMPLINK_BAR = {
	title: '解锁完整内容',
	unlockedTitle: '',
	promoTag: '限时优惠',
	price: '29.90',
	buttonColor: '#2461FF',
	jumpUrl: '',
	hideOtherPrice: false,
};

const PAY_UNLOCK_DEFAULTS = {
	'pay-unlock': DEFAULT_PAY_UNLOCK_CLASSIC,
	'pay-unlock-trial': DEFAULT_PAY_UNLOCK_TRIAL,
	'pay-unlock-limited': DEFAULT_PAY_UNLOCK_LIMITED,
	'pay-unlock-pack': DEFAULT_PAY_UNLOCK_PACK,
	'pay-unlock-community': DEFAULT_PAY_UNLOCK_COMMUNITY,
	'pay-unlock-image': DEFAULT_PAY_UNLOCK_IMAGE,
	'pay-unlock-overseas': DEFAULT_PAY_UNLOCK_OVERSEAS,
	'pay-unlock-creator': DEFAULT_PAY_UNLOCK_CREATOR,
	'pay-unlock-mystery': DEFAULT_PAY_UNLOCK_MYSTERY,
	'pay-unlock-jumplink': DEFAULT_PAY_UNLOCK_JUMPLINK,
	'pay-unlock-jumplink-plus': DEFAULT_PAY_UNLOCK_JUMPLINK_PLUS,
	'pay-unlock-jumplink-featured': DEFAULT_PAY_UNLOCK_JUMPLINK_FEATURED,
	'pay-unlock-jumplink-course': DEFAULT_PAY_UNLOCK_JUMPLINK_COURSE,
	'pay-unlock-jumplink-advance': DEFAULT_PAY_UNLOCK_JUMPLINK_ADVANCE,
	'pay-unlock-jumplink-unlock': DEFAULT_PAY_UNLOCK_JUMPLINK_UNLOCK,
	'pay-unlock-jumplink-viewall': DEFAULT_PAY_UNLOCK_JUMPLINK_VIEWALL,
	'pay-unlock-jumplink-bar': DEFAULT_PAY_UNLOCK_JUMPLINK_BAR,
};

export function getPayUnlockDefaultData(type) {
	return { ...(PAY_UNLOCK_DEFAULTS[type] || DEFAULT_PAY_UNLOCK_CLASSIC) };
}

const PAY_TOOLBAR_ACTIONS = [
	{ key: 'edit', label: '编辑' },
];

const QRCODE_NAV_TOOLBAR_ACTIONS = [
	{ key: 'copy', label: '复制' },
	{ key: 'edit', label: '编辑' },
];

const IMAGE_GRID_TOOLBAR_ACTIONS = [
	{ key: 'copy', label: '复制' },
	{ key: 'edit', label: '编辑' },
];

export const DEFAULT_QRCODE_NAV_DATA = getQrcodeNavDefaults('qrcode-nav-solid');
export const DEFAULT_QUICK_NAV_GRID_DATA = getQuickNavGridDefaults();
export const DEFAULT_IMAGE_GRID_DATA = getImageGridDefaults();
export const DEFAULT_IMAGE_CARD_DATA = getImageCardDefaults();
export const DEFAULT_IMAGE_FEATURE_CARD_DATA = getImageFeatureCardDefaults();
export const DEFAULT_IMAGE_COLLAGE_DATA = getImageCollageDefaults();
export const DEFAULT_IMAGE_TRIO_DATA = getImageTrioDefaults();
export const DEFAULT_IMAGE_MOSAIC_DATA = getImageMosaicDefaults();
export const DEFAULT_IMAGE_QUAD_DATA = getImageQuadDefaults();
export const DEFAULT_IMAGE_HEADER_DATA = getImageHeaderDefaults();
export const DEFAULT_FILE_HEADER_DATA = getFileHeaderDefaults();
export const DEFAULT_AUDIO_HEADER_DATA = getAudioHeaderDefaults();
export const DEFAULT_VIDEO_HEADER_DATA = getVideoHeaderDefaults();
export const DEFAULT_CONTACT_INFO_DATA = getContactInfoDefaults();
export const DEFAULT_VIDEO_LIST_DATA = getVideoListDefaults();
export const DEFAULT_VIDEO_ROW_DATA = getVideoRowDefaults();
export const DEFAULT_AUDIO_LIBRARY_DATA = getAudioLibraryDefaults();
export const DEFAULT_AUDIO_PLAYER_DATA = getAudioPlayerDefaults();
export const DEFAULT_PAY_IMAGE_GALLERY_DATA = getPayImageGalleryDefaults();
export const DEFAULT_PAY_IMAGE_WALL_DATA = getPayImageWallDefaults();
export const DEFAULT_PAY_FILE_UNLOCK_DATA = getPayFileUnlockDefaults();
export const DEFAULT_PAY_FILE_LIST_DATA = getPayFileListDefaults();
export const DEFAULT_PAY_FILE_COLLECTION_DATA = getPayFileCollectionDefaults();
export const DEFAULT_PAY_AUDIO_PLAYER_DATA = getPayAudioPlayerDefaults();
export const DEFAULT_PAY_AUDIO_LIBRARY_DATA = getPayAudioLibraryDefaults();
export const DEFAULT_PAY_AUDIO_PLAYLIST_DATA = getPayAudioPlaylistDefaults();
export const DEFAULT_PAY_VIDEO_PLAYER_DATA = getPayVideoPlayerDefaults();
export const DEFAULT_PAY_VIDEO_LIST_DATA = getPayVideoListDefaults();
export const DEFAULT_PAY_VIDEO_SERIES_DATA = getPayVideoSeriesDefaults();

function createQrcodeNavTemplate(type, label) {
	const defaults = getQrcodeNavDefaults(type);
	return {
		type,
		label,
		library: 'component',
		componentCategory: 'qrcodeNav',
		componentIcon: 'mbm-iconfont mbm-erweima',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: QRCODE_NAV_TOOLBAR_ACTIONS,
	};
}

function createQuickNavGridTemplate() {
	const defaults = getQuickNavGridDefaults();
	return {
		type: 'quick-nav-grid',
		label: '3列跳转',
		library: 'component',
		componentCategory: 'qrcodeNav',
		componentIcon: 'mbm-iconfont mbm-erweima',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: QRCODE_NAV_TOOLBAR_ACTIONS,
	};
}

function createImageGridTemplate() {
	const defaults = getImageGridDefaults();
	return {
		type: 'image-grid',
		label: '图片组件',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageCardTemplate() {
	const defaults = getImageCardDefaults();
	return {
		type: 'image-card',
		label: '图片卡片',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageFeatureCardTemplate() {
	const defaults = getImageFeatureCardDefaults();
	return {
		type: 'image-feature-card',
		label: '图文卡片',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageCollageTemplate() {
	const defaults = getImageCollageDefaults();
	return {
		type: 'image-collage',
		label: '图片拼贴',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageTrioTemplate() {
	const defaults = getImageTrioDefaults();
	return {
		type: 'image-trio',
		label: '三图拼贴',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageMosaicTemplate() {
	const defaults = getImageMosaicDefaults();
	return {
		type: 'image-mosaic',
		label: '图片宫格',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageQuadTemplate() {
	const defaults = getImageQuadDefaults();
	return {
		type: 'image-quad',
		label: '四列宫格',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createImageHeaderTemplate() {
	const defaults = getImageHeaderDefaults();
	return {
		type: 'image-header',
		label: '图片',
		library: 'component',
		componentCategory: 'image',
		componentIcon: 'mbm-iconfont mbm-tupian',
		hideFromLibrary: true,
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createFileHeaderTemplate() {
	const defaults = getFileHeaderDefaults();
	return {
		type: 'file-header',
		label: '文件',
		library: 'component',
		componentCategory: 'file',
		componentIcon: 'mbm-iconfont mbm-wenjian',
		hideFromLibrary: true,
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createAudioHeaderTemplate() {
	const defaults = getAudioHeaderDefaults();
	return {
		type: 'audio-header',
		label: '音频',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		hideFromLibrary: true,
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createVideoHeaderTemplate() {
	const defaults = getVideoHeaderDefaults();
	return {
		type: 'video-header',
		label: '视频',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-shipin',
		hideFromLibrary: true,
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createContactInfoTemplate() {
	const defaults = getContactInfoDefaults();
	return {
		type: 'contact-info',
		label: '联系方式',
		library: 'component',
		componentCategory: 'contact',
		componentIcon: 'mbm-iconfont mbm-dianhua',
		hideFromLibrary: true,
		createData() {
			return { ...defaults };
		},
		previewData() {
			return {
				...defaults,
				title: '标题',
				phones: ['15555555555', '13333333333'],
				email: '404657437@qq.com',
				address: '四川省成都市武侯区桂溪街道交子南二路',
				extraItems: [{ id: 'preview-company', type: 'company', value: '测试部门' }],
				remark: '测试备注',
			};
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createRichTextTemplate() {
	return {
		type: 'rich-text',
		label: '富文本',
		library: 'component',
		componentCategory: 'richText',
		componentIcon: 'mbm-iconfont mbm-fuwenben',
		hideFromLibrary: true,
		createData() {
			return { content: '' };
		},
		previewData() {
			return { content: '' };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createVideoListTemplate() {
	const defaults = getVideoListDefaults();
	return {
		type: 'video-list',
		label: '视频列表',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-shipin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createVideoRowTemplate() {
	const defaults = getVideoRowDefaults();
	return {
		type: 'video-row',
		label: '列表视频',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-shipin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createAudioLibraryTemplate() {
	const defaults = getAudioLibraryDefaults();
	return {
		type: 'audio-library',
		label: '音频库',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createAudioPlayerTemplate() {
	const defaults = getAudioPlayerDefaults();
	return {
		type: 'audio-player',
		label: '音频播放',
		library: 'component',
		componentCategory: 'media',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createHeaderCarouselBannerTemplate() {
	const defaults = getHeaderCarouselBannerDefaults();
	return {
		type: 'header-carousel',
		label: '头部轮播',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createHeaderCarouselImageTemplate() {
	const defaults = getHeaderCarouselImageDefaults();
	return {
		type: 'header-carousel-image',
		label: '图片轮播',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createHeaderHeroTemplate() {
	const defaults = getHeaderHeroDefaults();
	return {
		type: 'header-hero',
		label: '头部展示',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-biaoti',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createHeaderInfoCardTemplate() {
	const defaults = getHeaderInfoCardDefaults();
	return {
		type: 'header-info-card',
		label: '图文头部',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-wenjian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createHeaderInfoCardV2Template() {
	const defaults = getHeaderInfoCardV2Defaults();
	return {
		type: 'header-info-card-v2',
		label: '图文卡片头部',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-wenjian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createHeaderIpBrandTemplate() {
	const defaults = getHeaderIpBrandDefaults();
	return {
		type: 'header-ip-brand',
		label: 'IP品牌头部',
		library: 'component',
		componentCategory: 'header',
		headerComponent: true,
		componentIcon: 'mbm-iconfont mbm-zhuye',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
	};
}

function createPayImageGalleryTemplate() {
	const defaults = getPayImageGalleryDefaults();
	return {
		type: 'pay-image-gallery',
		label: '图片宫格付费',
		library: 'component',
		componentCategory: 'payImage',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayImageWallTemplate() {
	const defaults = getPayImageWallDefaults();
	return {
		type: 'pay-image-wall',
		label: '图片列表付费',
		library: 'component',
		componentCategory: 'payImage',
		componentIcon: 'mbm-iconfont mbm-tupian',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayFileUnlockTemplate() {
	return {
		type: 'pay-file-unlock',
		label: '文件解锁付费',
		library: 'component',
		componentCategory: 'payFile',
		componentIcon: 'mbm-iconfont mbm-wenjian',
		createData() {
			return getPayFileUnlockPreviewDefaults();
		},
		previewData() {
			return getPayFileUnlockPreviewDefaults();
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayFileListTemplate() {
	return {
		type: 'pay-file-list',
		label: '文件列表付费',
		library: 'component',
		componentCategory: 'payFile',
		componentIcon: 'mbm-iconfont mbm-wenjian',
		createData() {
			return getPayFileListPreviewDefaults();
		},
		previewData() {
			return getPayFileListPreviewDefaults();
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayFileCollectionTemplate() {
	return {
		type: 'pay-file-collection',
		label: '文档合集付费',
		library: 'component',
		componentCategory: 'payFile',
		componentIcon: 'mbm-iconfont mbm-wenjian',
		createData() {
			return getPayFileCollectionPreviewDefaults();
		},
		previewData() {
			return getPayFileCollectionPreviewDefaults();
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayAudioPlayerTemplate() {
	const defaults = getPayAudioPlayerDefaults();
	const previewDefaults = getPayAudioPlayerPreviewDefaults();
	return {
		type: 'pay-audio-player',
		label: '单音频付费',
		library: 'component',
		componentCategory: 'payAudio',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayAudioLibraryTemplate() {
	const defaults = getPayAudioLibraryDefaults();
	const previewDefaults = getPayAudioLibraryPreviewDefaults();
	return {
		type: 'pay-audio-library',
		label: '多音频付费',
		library: 'component',
		componentCategory: 'payAudio',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayAudioPlaylistTemplate() {
	const defaults = getPayAudioPlaylistDefaults();
	const previewDefaults = getPayAudioPlaylistPreviewDefaults();
	return {
		type: 'pay-audio-playlist',
		label: '歌单付费',
		library: 'component',
		componentCategory: 'payAudio',
		componentIcon: 'mbm-iconfont mbm-yinpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayVideoPlayerTemplate() {
	const defaults = getPayVideoPlayerDefaults();
	const previewDefaults = getPayVideoPlayerPreviewDefaults();
	return {
		type: 'pay-video-player',
		label: '单视频付费',
		library: 'component',
		componentCategory: 'payVideo',
		componentIcon: 'mbm-iconfont mbm-shipin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayVideoListTemplate() {
	const defaults = getPayVideoListDefaults();
	const previewDefaults = getPayVideoListPreviewDefaults();
	return {
		type: 'pay-video-list',
		label: '多视频付费',
		library: 'component',
		componentCategory: 'payVideo',
		componentIcon: 'mbm-iconfont mbm-shipin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayVideoSeriesTemplate() {
	const defaults = getPayVideoSeriesDefaults();
	const previewDefaults = getPayVideoSeriesPreviewDefaults();
	return {
		type: 'pay-video-series',
		label: '剧集视频付费',
		library: 'component',
		componentCategory: 'payVideo',
		componentIcon: 'mbm-iconfont mbm-shipin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...previewDefaults };
		},
		toolbarActions: IMAGE_GRID_TOOLBAR_ACTIONS,
	};
}

function createPayTemplate(type, label, defaults, componentCategory = 'payGlobal') {
	return {
		type,
		label,
		library: 'component',
		componentCategory,
		componentIcon: 'mbm-iconfont mbm-shangpin',
		fixedPosition: 'bottom',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: PAY_TOOLBAR_ACTIONS,
	};
}

function createPayTemplateNoFixed(type, label, defaults, componentCategory = 'payGlobal') {
	return {
		type,
		label,
		library: 'component',
		componentCategory,
		componentIcon: 'mbm-iconfont mbm-shangpin',
		createData() {
			return { ...defaults };
		},
		previewData() {
			return { ...defaults };
		},
		toolbarActions: PAY_TOOLBAR_ACTIONS,
	};
}

export function getGemColors(data) {
	// 优先读取新的单色配置
	const singleColor = data?.gemColor;
	if (typeof singleColor === 'string' && singleColor.trim()) {
		return [singleColor.trim()];
	}
	// 兼容旧的数组格式
	const colors = data?.gemColors;
	if (Array.isArray(colors) && colors.length > 0) {
		return colors.map(color => String(color));
	}
	return [...DEFAULT_GEM_COLORS];
}

export function getTitleThemeColor(data) {
	const color = data?.color;
	if (typeof color === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(color.trim())) {
		return color.trim().toLowerCase();
	}
	return DEFAULT_TITLE_THEME_COLOR;
}

export function getTitleFaqColor(data) {
	return getTitleThemeColor(data);
}

function getHexColor(value, fallback) {
	if (typeof value === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(value.trim())) {
		return value.trim().toLowerCase();
	}
	return fallback;
}

export function getTitleTagColor(data) {
	return getHexColor(data?.color, DEFAULT_TITLE_TAG_COLOR);
}

/** 线框斜标正文双色：[边框, 斜标] */
export function getBodyFrameIconColors(data) {
	const colors = data?.frameColors;
	if (Array.isArray(colors) && colors.length === 2) {
		return colors.map((color, index) => getHexColor(color, DEFAULT_BODY_FRAME_ICON_COLORS[index]));
	}
	if (data?.borderColor || data?.color) {
		return [
			getHexColor(data?.borderColor, DEFAULT_BODY_FRAME_ICON_COLORS[0]),
			getHexColor(data?.color, DEFAULT_BODY_FRAME_ICON_COLORS[1]),
		];
	}
	return [...DEFAULT_BODY_FRAME_ICON_COLORS];
}

/** 色块正文背景色 */
export function getBodyBgColor(data) {
	return getHexColor(data?.color, DEFAULT_BODY_BG_COLOR);
}

/** 色块标题背景：左深右浅，约 90% 处完全透明 */
export function getTitleTagBgGradient(data) {
	const color = getTitleTagColor(data);
	return `linear-gradient(to right, ${hexToRgba(color, 0.28)} 0%, transparent 90%)`;
}

/** 是否为固定在底部的区块（如支付组件） */
export function isFixedBottomBlock(type) {
	return TEMPLATE_MAP[type]?.fixedPosition === 'bottom';
}

/** 将 blocks 拆分为普通流式区块与底部固定区块 */
export function splitBlocks(blocks = []) {
	const scrollBlocks = [];
	const fixedBottomBlocks = [];

	blocks.forEach(block => {
		if (isFixedBottomBlock(block.type)) {
			fixedBottomBlocks.push(block);
		} else {
			scrollBlocks.push(block);
		}
	});

	return { scrollBlocks, fixedBottomBlocks };
}

const DUAL_TITLE_TOOLBAR = [
	{ key: 'copy', label: '复制' },
	{ key: 'theme-color', type: 'color-single' },
];

const BODY_TOOLBAR = [
	{ key: 'copy', label: '复制' },
];

const BODY_ACCENT_TOOLBAR = [
	{ key: 'copy', label: '复制' },
	{ key: 'theme-color', type: 'color-single' },
];

function createBodyTemplate(type, label, content = '') {
	return {
		type,
		label,
		category: 'body',
		createData() {
			return { content: '' };
		},
		previewData() {
			return { content };
		},
		toolbarActions: BODY_TOOLBAR,
	};
}

function createBodyAccentTemplate(type, label, content) {
	return {
		type,
		label,
		category: 'body',
		createData() {
			return {
				content: '',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				content,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

function createBodyBgTemplate(type, label, content) {
	return {
		type,
		label,
		category: 'body',
		createData() {
			return {
				content: '',
				color: DEFAULT_BODY_BG_COLOR,
			};
		},
		previewData() {
			return {
				content,
				color: DEFAULT_BODY_BG_COLOR,
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

function createBodyCardAccentTemplate(type, label, title, content) {
	return {
		type,
		label,
		category: 'body',
		createData() {
			return {
				title: '',
				content: '',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				title,
				content,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

function createBodyIndexAccentTemplate(type, label, content, index = '01') {
	return {
		type,
		label,
		category: 'body',
		createData() {
			return {
				content: '',
				color: DEFAULT_TITLE_THEME_COLOR,
				index,
			};
		},
		previewData() {
			return {
				content,
				color: DEFAULT_TITLE_THEME_COLOR,
				index,
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

function createDividerTemplate(type, label) {
	return {
		type,
		label,
		category: 'divider',
		createData() {
			return {
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

function createDividerGrayBarTemplate(type, label, color = '#e5e7eb') {
	return {
		type,
		label,
		category: 'divider',
		createData() {
			return { color };
		},
		previewData() {
			return { color };
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	};
}

const BODY_FRAME_ICON_TOOLBAR = [
	{ key: 'copy', label: '复制' },
	{ key: 'frame-colors', type: 'color-dual', labels: ['边框', '斜标'] },
];

function createBodyFrameIconTemplate(type, label, content) {
	const frameColors = [...DEFAULT_BODY_FRAME_ICON_COLORS];
	return {
		type,
		label,
		category: 'body',
		createData() {
			return {
				content: '',
				frameColors: [...frameColors],
			};
		},
		previewData() {
			return {
				content,
				frameColors: [...frameColors],
			};
		},
		toolbarActions: BODY_FRAME_ICON_TOOLBAR,
	};
}

function createDualTitleTemplate(type, label, primaryText, secondaryText) {
	return {
		type,
		label,
		category: 'title',
		createData() {
			return {
				primaryText,
				secondaryText,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				primaryText,
				secondaryText,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: DUAL_TITLE_TOOLBAR,
	};
}

function createArticleTemplate() {
	return {
		type: 'title-article',
		label: '文章标题',
		category: 'title',
		createData() {
			return {
				primaryText: '文章标题',
				metaText: '作者 2023-11-04 23:14 发表于浙江',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				primaryText: '文章标题',
				metaText: '作者 2023-11-04 23:14 发表于浙江',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: DUAL_TITLE_TOOLBAR,
	};
}

function createHeadingTemplate(level) {
	const label = `标题${level}`;
	return {
		type: `title-h${level}`,
		label,
		category: 'title',
		createData() {
			return {
				content: label,
				level,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				content: label,
				level,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: DUAL_TITLE_TOOLBAR,
	};
}

function createSingleTitleTemplate(type, label, content) {
	return {
		type,
		label,
		category: 'title',
		createData() {
			return {
				content,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				content,
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: DUAL_TITLE_TOOLBAR,
	};
}

function createRecruitCardTemplate() {
	return {
		type: 'title-recruit-card',
		label: '招聘卡片标题',
		category: 'title',
		createData() {
			return {
				primaryText: '2020招聘全面开启',
				secondaryText: '提片网',
				buttonText: 'JOIN US',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				primaryText: '2020招聘全面开启',
				secondaryText: '提片网',
				buttonText: 'JOIN US',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: DUAL_TITLE_TOOLBAR,
	};
}

/**
 * 模板注册表（PC 编辑 & H5 渲染共用）
 * - type: 区块类型，与 blocks 目录下组件一一对应
 * - category: 样式库分类（title / body / divider / ungrouped）
 * - createData: 插入时的默认数据
 * - previewData: 样式库左侧预览用的示例数据
 */
export const TEMPLATE_MAP = {
	'title-basic': {
		type: 'title-basic',
		label: '基础标题',
		category: 'title',
		createData() {
			return {
				content: '基础标题',
			};
		},
		previewData() {
			return {
				content: '基础标题',
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
		],
	},
	'title-diamond': {
		type: 'title-diamond',
		label: '菱形装饰标题',
		category: 'title',
		createData() {
			return {
				content: '企业简介',
				gemColor: '#285af9',
			};
		},
		previewData() {
			return {
				content: '企业简介',
				gemColor: '#285af9',
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'gem-color', type: 'color-picker' },
		],
	},
	'title-faq': {
		type: 'title-faq',
		label: 'FAQ标题',
		category: 'title',
		createData() {
			return {
				primaryText: 'FAQ',
				secondaryText: '常见问题',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				primaryText: 'FAQ',
				secondaryText: '常见问题',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'theme-color', type: 'color-single' },
		],
	},
	'title-brand': {
		type: 'title-brand',
		label: '品牌故事标题',
		category: 'title',
		createData() {
			return {
				primaryText: '品牌故事',
				secondaryText: 'Brand Story',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				primaryText: '品牌故事',
				secondaryText: 'Brand Story',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'theme-color', type: 'color-single' },
		],
	},
	'title-tag': {
		type: 'title-tag',
		label: '色块标题',
		category: 'title',
		createData() {
			return {
				primaryText: '产品特点',
				secondaryText: 'Product Features',
				color: DEFAULT_TITLE_TAG_COLOR,
			};
		},
		previewData() {
			return {
				primaryText: '产品特点',
				secondaryText: 'Product Features',
				color: DEFAULT_TITLE_TAG_COLOR,
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'theme-color', type: 'color-single' },
		],
	},
	'title-underline': {
		type: 'title-underline',
		label: '下划线标题',
		category: 'title',
		createData() {
			return {
				content: '产品展示',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				content: '产品展示',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'theme-color', type: 'color-single' },
		],
	},
	'title-pill': {
		type: 'title-pill',
		label: '胶囊标题',
		category: 'title',
		createData() {
			return {
				content: 'AI智能系统芯片',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		previewData() {
			return {
				content: 'AI智能系统芯片',
				color: DEFAULT_TITLE_THEME_COLOR,
			};
		},
		toolbarActions: [
			{ key: 'copy', label: '复制' },
			{ key: 'theme-color', type: 'color-single' },
		],
	},
	'title-sidebar-en': createDualTitleTemplate(
		'title-sidebar-en',
		'竖线英文副标题',
		'产品检测报告',
		'PRODUCT TESTING REPORT',
	),
	'title-sidebar': createDualTitleTemplate(
		'title-sidebar',
		'竖线副标题',
		'会议介绍',
		'2024高端数字经济论坛',
	),
	'title-stack': createDualTitleTemplate(
		'title-stack',
		'堆叠标题',
		'雷蛇高效无线机械键盘',
		'高端品质，匠心制造',
	),
	'title-stack-reverse': createDualTitleTemplate(
		'title-stack-reverse',
		'反向堆叠标题',
		'大功率工业级电机',
		'耐高温·高负载·寿命长',
	),
	'title-en-lead': createDualTitleTemplate(
		'title-en-lead',
		'英文主标题',
		'MAKE LIFE BETTER',
		'让生活更美好',
	),
	'title-left-lead': createDualTitleTemplate(
		'title-left-lead',
		'左对齐大标题',
		'Nordic Lighting',
		'简约创意吊灯',
	),
	'title-center-underline': createDualTitleTemplate(
		'title-center-underline',
		'居中下划线标题',
		'Selected',
		'环球之旅精选咖啡礼盒',
	),
	'title-center-stack': createDualTitleTemplate(
		'title-center-stack',
		'居中堆叠标题',
		'PM68系列呼吸机',
		'韵律呼吸，优雅绽放',
	),
	'title-center-divider': createDualTitleTemplate(
		'title-center-divider',
		'居中短分隔标题',
		'多种安装方式\n适用多种场景安装',
		'壁挂、橱下、橱上均可自由安装\n简便拆装，自主换芯',
	),
	'title-full-line': createDualTitleTemplate(
		'title-full-line',
		'全宽线标题',
		'关于我们',
		'ABOUT US',
	),
	'title-center-bar': createDualTitleTemplate(
		'title-center-bar',
		'居中横线标题',
		'SL508传感器激光焊接机',
		'核心技术',
	),
	'title-article': createArticleTemplate(),
	'title-h1': createHeadingTemplate(1),
	'title-h2': createHeadingTemplate(2),
	'title-h3': createHeadingTemplate(3),
	'title-h4': createHeadingTemplate(4),
	'title-h5': createHeadingTemplate(5),
	'title-h6': createHeadingTemplate(6),
	'title-trail-line': createSingleTitleTemplate('title-trail-line', '延伸线标题', '企业文化'),
	'title-thick-underline': createSingleTitleTemplate('title-thick-underline', '粗下划线标题', '全铜材质，可用50年'),
	'title-center-rail': createSingleTitleTemplate('title-center-rail', '轨道线标题', '办事指南'),
	'title-center-break': createSingleTitleTemplate('title-center-break', '断线标题', '景点介绍'),
	'title-badge': createSingleTitleTemplate('title-badge', '色块胶囊标题', '企业文化'),
	'title-framed': createSingleTitleTemplate('title-framed', '双边框标题', '公司企业文化'),
	'title-double-line': createSingleTitleTemplate('title-double-line', '双横线标题', '中央净水系列'),
	'title-recruit-card': createRecruitCardTemplate(),
	'title-left-bar': createSingleTitleTemplate('title-left-bar', '左竖条标题', '交底文件'),
	'title-left-bar-bg': createSingleTitleTemplate('title-left-bar-bg', '灰底竖条标题', '其他信息'),
	'title-center-side-line': createSingleTitleTemplate('title-center-side-line', '居中侧线标题', '2月10日-工地例会'),
	'title-slant-block': createSingleTitleTemplate('title-slant-block', '斜翼色块标题', '企业介绍'),
	'title-box-accent': createSingleTitleTemplate('title-box-accent', '线框底标标题', '会议议程'),
	'title-split-box': createDualTitleTemplate('title-split-box', '分色双块标题', '会议', '会议主题'),
	'title-slant-tag': createDualTitleTemplate('title-slant-tag', '斜标组合标题', '项目一', '项目名称'),
	'title-slant-lines': createSingleTitleTemplate('title-slant-lines', '斜线装饰标题', '景点介绍'),
	'title-box-side-line': createSingleTitleTemplate('title-box-side-line', '色块侧线标题', '产品介绍'),
	'title-diamond-accent': createSingleTitleTemplate('title-diamond-accent', '菱形点缀标题', '旅游行程'),
	'body-basic': createBodyTemplate('body-basic', '基础正文', '请输入正文'),
	'body-bg': createBodyBgTemplate(
		'body-bg',
		'色块正文',
		'公司莎车西站铁路物流专用线项目新建信号综合楼框架主题混凝土浇筑顺利完成。',
	),
	'body-bullet': createBodyAccentTemplate(
		'body-bullet',
		'圆点正文',
		'严格落实值班制度，制定完善开学准备工作方案、疫情防控应急预案、疫情报告制度、晨检制度。',
	),
	'body-line-border': createBodyAccentTemplate(
		'body-line-border',
		'双线正文',
		'提片是一个简单实用的知识付费系统搭建平台，提供标准化的功能和服务模块，可以灵活组合，根据实际需求搭建知识付费业务系统。',
	),
	'body-frame-icon': createBodyFrameIconTemplate(
		'body-frame-icon',
		'线框斜标正文',
		'《通知》要求，物业服务机构要根据服务业态特点制定写字楼物业管理区域回蓉返岗复工疫情防控工作预案，精准施策，不断提高写字楼防控工作的针对性有效性。',
	),
	'body-corner-bracket': createBodyAccentTemplate(
		'body-corner-bracket',
		'角标正文',
		'《通知》要求，要做好疫情期间特殊有害垃圾的收集和处置，在公共场所醒目位置设置专门的废弃口罩等特殊有害垃圾收集点、专用收集桶等，引导写字楼办公人员严格按要求将废弃口罩丢弃指定位置。',
	),
	'body-left-bar': createBodyCardAccentTemplate(
		'body-left-bar',
		'左线卡片',
		'活动流程',
		'1. 报道，集合\n2. 团队活动/参观园区\n3. 自由活动时间\n4. 返程',
	),
	'body-split-header': createBodyCardAccentTemplate(
		'body-split-header',
		'双色头卡片',
		'酒庄介绍',
		'酒庄始建于1892年，占地面积约200亩，是集葡萄种植、酿造、观光旅游为一体的综合性酒庄。这里气候宜人，土壤肥沃，是酿造优质葡萄酒的理想之地。',
	),
	'body-line-badge': createBodyCardAccentTemplate(
		'body-line-badge',
		'中线标题卡片',
		'灭火器使用说明',
		'1. 取出灭火器，拔掉保险销\n2. 一手握住压把，一手握住喷管\n3. 对准火焰根部，压下压把喷射\n4. 左右扫射，直至火焰熄灭',
	),
	'body-solid': createBodyAccentTemplate(
		'body-solid',
		'纯色正文卡片',
		'公司莎车西站铁路物流专用线项目新建信号综合楼框架主题混凝土浇筑顺利完成。',
	),
	'body-notice-box': createBodyCardAccentTemplate(
		'body-notice-box',
		'通知边框卡片',
		'通知公告',
		'因电力设备检修，本小区将于本周六上午9:00至下午17:00暂停供电，请各住户提前做好准备。如有疑问，请联系物业服务中心。',
	),
	'body-index-side': createBodyIndexAccentTemplate(
		'body-index-side',
		'序号侧线正文',
		'公司莎车西站铁路物流专用线项目新建信号综合楼框架主题混凝土浇筑顺利完成。',
		'01',
	),
	'body-dashed-title': createBodyCardAccentTemplate(
		'body-dashed-title',
		'虚线标题正文',
		'产品介绍',
		'奔富 Penfolds 是澳大利亚著名的葡萄酒品牌，1844年由克里斯托弗·罗森·奔富在阿德莱德的玛格尔庄园创立。经过170多年的发展，已成为享誉全球的葡萄酒品牌。',
	),
	'body-outline': createBodyAccentTemplate(
		'body-outline',
		'实线边框正文',
		'根据《中华人民共和国道路交通安全法》规定，机动车应当定期进行安全技术检验。对符合机动车国家安全技术标准的，公安机关交通管理部门应当发给检验合格标志。',
	),
	'body-bar-panel': createBodyCardAccentTemplate(
		'body-bar-panel',
		'左条灰底卡片',
		'护照办理流程',
		'1. 准备材料：身份证、户口本、照片等\n2. 网上预约或现场取号\n3. 提交申请并缴费\n4. 等待审核，领取护照',
	),
	'body-gray-tip': createBodyCardAccentTemplate(
		'body-gray-tip',
		'灰框提示正文',
		'温馨提示',
		'1. 请提前30分钟到达集合地点\n2. 请携带有效身份证件\n3. 如有特殊情况请提前联系工作人员\n4. 活动期间请听从工作人员安排',
	),
	'body-line-dot-right': createBodyCardAccentTemplate(
		'body-line-dot-right',
		'右标题线点正文',
		'通知公告',
		'年度优秀员工晋升申请启动了。以下是优秀员工申请的注意事项，请认真阅读。',
	),
	'body-line-dot-left': createBodyCardAccentTemplate(
		'body-line-dot-left',
		'左标题线点正文',
		'通知公告',
		'本年度优秀员工晋升申请启动了。以下是优秀员工申请的注意事项，请认真阅读。',
	),
	'body-bracket-line': {
		type: 'body-bracket-line',
		label: '括号线标题正文',
		category: 'body',
		createData() {
			return {
				title: '',
				content: '',
				color: '#dcdfe6',
			};
		},
		previewData() {
			return {
				title: '[ 员工福利 ]',
				content: '舒适的办公环境，免费早餐、免费班车、免费健身房，茶水间咖啡茶饮不间断供应；',
				color: '#dcdfe6',
			};
		},
		toolbarActions: BODY_ACCENT_TOOLBAR,
	},
	'divider-thin': createDividerTemplate('divider-thin', '细线分割'),
	'divider-thick': createDividerTemplate('divider-thick', '粗线分割'),
	'divider-double-thick-top': createDividerTemplate('divider-double-thick-top', '上粗下细'),
	'divider-double-mixed': createDividerTemplate('divider-double-mixed', '上细下粗'),
	'divider-gradient': createDividerTemplate('divider-gradient', '渐变分割'),
	'divider-dashed': createDividerTemplate('divider-dashed', '虚线分割'),
	'divider-dashed-dense': createDividerTemplate('divider-dashed-dense', '密虚线分割'),
	'divider-dashed-sparse': createDividerTemplate('divider-dashed-sparse', '疏虚线分割'),
	'divider-center-squares': createDividerTemplate('divider-center-squares', '双方块分割'),
	'divider-center-diamond': createDividerTemplate('divider-center-diamond', '菱形分割'),
	'divider-gray-bar': createDividerGrayBarTemplate('divider-gray-bar', '灰条分割'),
	'pay-unlock': createPayTemplate('pay-unlock', '经典版', DEFAULT_PAY_UNLOCK_CLASSIC),
	'pay-unlock-trial': createPayTemplate('pay-unlock-trial', '试看版', DEFAULT_PAY_UNLOCK_TRIAL),
	'pay-unlock-limited': createPayTemplate('pay-unlock-limited', '限时版', DEFAULT_PAY_UNLOCK_LIMITED),
	'pay-unlock-pack': createPayTemplate('pay-unlock-pack', '资料包版', DEFAULT_PAY_UNLOCK_PACK),
	'pay-unlock-community': createPayTemplate('pay-unlock-community', '社群版', DEFAULT_PAY_UNLOCK_COMMUNITY),
	'pay-unlock-image': createPayTemplate('pay-unlock-image', '图片付费版', DEFAULT_PAY_UNLOCK_IMAGE),
	'pay-unlock-overseas': createPayTemplate('pay-unlock-overseas', '海外版', DEFAULT_PAY_UNLOCK_OVERSEAS),
	'pay-unlock-creator': createPayTemplate('pay-unlock-creator', '创作者收益版', DEFAULT_PAY_UNLOCK_CREATOR),
	'pay-unlock-mystery': createPayTemplate('pay-unlock-mystery', '神秘版', DEFAULT_PAY_UNLOCK_MYSTERY),
	'pay-unlock-jumplink-unlock': createPayTemplateNoFixed('pay-unlock-jumplink-unlock', '解锁完整版', DEFAULT_PAY_UNLOCK_JUMPLINK_UNLOCK, 'payLink'),
	'pay-unlock-jumplink-viewall': createPayTemplateNoFixed('pay-unlock-jumplink-viewall', '查看全部版', DEFAULT_PAY_UNLOCK_JUMPLINK_VIEWALL, 'payLink'),
	'pay-unlock-jumplink-bar': createPayTemplateNoFixed('pay-unlock-jumplink-bar', '限时优惠版', DEFAULT_PAY_UNLOCK_JUMPLINK_BAR, 'payLink'),
	'pay-unlock-jumplink-plus': createPayTemplateNoFixed('pay-unlock-jumplink-plus', '跳转链接加强版', DEFAULT_PAY_UNLOCK_JUMPLINK_PLUS, 'payLink'),
	'pay-unlock-jumplink': createPayTemplateNoFixed('pay-unlock-jumplink', '跳转链接版', DEFAULT_PAY_UNLOCK_JUMPLINK, 'payLink'),
	'pay-unlock-jumplink-featured': createPayTemplateNoFixed('pay-unlock-jumplink-featured', '付费跳转链接', DEFAULT_PAY_UNLOCK_JUMPLINK_FEATURED, 'payLink'),
	'pay-unlock-jumplink-course': createPayTemplateNoFixed('pay-unlock-jumplink-course', '实战课程版', DEFAULT_PAY_UNLOCK_JUMPLINK_COURSE, 'payLink'),
	'pay-unlock-jumplink-advance': createPayTemplateNoFixed('pay-unlock-jumplink-advance', '变现进阶版', DEFAULT_PAY_UNLOCK_JUMPLINK_ADVANCE, 'payLink'),
	
	'qrcode-nav-solid': createQrcodeNavTemplate('qrcode-nav-solid', '实心按钮'),
	'qrcode-nav-gradient': createQrcodeNavTemplate('qrcode-nav-gradient', '渐变胶囊'),
	'qrcode-nav-outline': createQrcodeNavTemplate('qrcode-nav-outline', '描边胶囊'),
	'qrcode-nav-cell': createQrcodeNavTemplate('qrcode-nav-cell', '图文导航'),
	'qrcode-nav-card': createQrcodeNavTemplate('qrcode-nav-card', '卡片导航'),
	'qrcode-nav-list': createQrcodeNavTemplate('qrcode-nav-list', '列表导航'),
	'qrcode-nav-banner': createQrcodeNavTemplate('qrcode-nav-banner', '横幅导航'),
	'qrcode-nav-link': createQrcodeNavTemplate('qrcode-nav-link', '跳转链接'),
	'qrcode-nav-grid': createQrcodeNavTemplate('qrcode-nav-grid', '跳转快捷'),
	'quick-nav-grid': createQuickNavGridTemplate(),
	'image-grid': createImageGridTemplate(),
	'image-card': createImageCardTemplate(),
	'image-feature-card': createImageFeatureCardTemplate(),
	'image-collage': createImageCollageTemplate(),
	'image-trio': createImageTrioTemplate(),
	'image-mosaic': createImageMosaicTemplate(),
	'image-quad': createImageQuadTemplate(),
	'image-header': createImageHeaderTemplate(),
	'file-header': createFileHeaderTemplate(),
	'audio-header': createAudioHeaderTemplate(),
	'video-header': createVideoHeaderTemplate(),
	'contact-info': createContactInfoTemplate(),
	'rich-text': createRichTextTemplate(),
	'video-list': createVideoListTemplate(),
	'video-row': createVideoRowTemplate(),
	'audio-library': createAudioLibraryTemplate(),
	'audio-player': createAudioPlayerTemplate(),
	'header-ip-brand': createHeaderIpBrandTemplate(),
	'header-carousel-image': createHeaderCarouselImageTemplate(),
	'header-hero': createHeaderHeroTemplate(),
	'header-carousel': createHeaderCarouselBannerTemplate(),
	'header-info-card': createHeaderInfoCardTemplate(),
	'header-info-card-v2': createHeaderInfoCardV2Template(),
	'pay-image-gallery': createPayImageGalleryTemplate(),
	'pay-image-wall': createPayImageWallTemplate(),
	'pay-file-unlock': createPayFileUnlockTemplate(),
	'pay-file-list': createPayFileListTemplate(),
	'pay-file-collection': createPayFileCollectionTemplate(),
	'pay-audio-player': createPayAudioPlayerTemplate(),
	'pay-audio-library': createPayAudioLibraryTemplate(),
	'pay-audio-playlist': createPayAudioPlaylistTemplate(),
	'pay-video-list': createPayVideoListTemplate(),
	'pay-video-player': createPayVideoPlayerTemplate(),

	'pay-video-series': createPayVideoSeriesTemplate(),
};

/** 组件类型 → 固定 6 位数字 id（同 type 永远相同） */
export const FIXED_BLOCK_ID_MAP = buildFixedBlockIdMap(TEMPLATE_MAP);

export function getBlockTypeId(type) {
	return getFixedBlockId(type, TEMPLATE_MAP[type], FIXED_BLOCK_ID_MAP);
}

/** 是否为全局支付组件（每页仅允许一个，固定底部） */
export function isGlobalPayComponentBlock(type) {
	const template = TEMPLATE_MAP[type];
	return template?.library === 'component' && template?.componentCategory === 'payGlobal';
}

/** 是否为支付类组件（每页仅允许一个）— 全局支付 + 跳转链接 */
export function isPayComponentBlock(type) {
	const template = TEMPLATE_MAP[type];
	if (template?.library !== 'component') return false;
	return template?.componentCategory === 'payGlobal' || template?.componentCategory === 'payLink';
}

/** 是否为头部组件（添加到页面头部区域，非正文 block） */
export function isHeaderComponent(type) {
	const template = TEMPLATE_MAP[type];
	return template?.library === 'component' && template?.headerComponent === true;
}

export { isQrcodeNavListBlock, isQrcodeNavLinkBlock, isQrcodeNavGridBlock } from './utils/qrcodeNav.js';
export { isQuickNavGridBlock } from './utils/quickNav.js';
export { isImageGridBlock } from './utils/imageGrid.js';
export { isImageCardBlock } from './utils/imageCard.js';
export { isImageFeatureCardBlock } from './utils/imageFeatureCard.js';
export { isImageCollageBlock } from './utils/imageCollage.js';
export { isImageTrioBlock } from './utils/imageTrio.js';
export { isImageMosaicBlock } from './utils/imageMosaic.js';
export { isImageQuadBlock } from './utils/imageQuad.js';
export { isImageHeaderBlock } from './utils/imageHeader.js';
export { isFileHeaderBlock } from './utils/fileHeader.js';
export { isAudioHeaderBlock } from './utils/audioHeader.js';
export { isVideoHeaderBlock } from './utils/videoHeader.js';
export { isContactInfoBlock } from './utils/contactInfo.js';

/** 是否为富文本区块 */
export function isRichTextBlock(type) {
	const template = TEMPLATE_MAP[type];
	return template?.library === 'component' && template?.componentCategory === 'richText';
}

export { isVideoListBlock } from './utils/videoList.js';
export { isVideoRowBlock } from './utils/videoRow.js';
export { isAudioLibraryBlock } from './utils/audioLibrary.js';
export { isAudioPlayerBlock } from './utils/audioPlayer.js';
export { isHeaderCarouselComponent } from './utils/headerCarousel.js';
export { isHeaderHeroComponent } from './utils/headerHero.js';
export { isHeaderInfoCardComponent } from './utils/headerInfoCard.js';
export { isHeaderInfoCardV2Component } from './utils/headerInfoCardV2.js';
export { isHeaderIpBrandComponent } from './utils/headerIpBrand.js';
export { isPayImageGalleryBlock } from './utils/payImageGallery.js';
export { isPayImageWallBlock } from './utils/payImageWall.js';
export { isPayFileUnlockBlock } from './utils/payFileUnlock.js';
export { isPayFileListBlock, isPayFileEditBlock } from './utils/payFileList.js';
export { isPayFileCollectionBlock } from './utils/payFileCollection.js';
export { isPayAudioPlayerBlock } from './utils/payAudioPlayer.js';
export { isPayAudioLibraryBlock } from './utils/payAudioLibrary.js';
export { isPayAudioPlaylistBlock } from './utils/payAudioPlaylist.js';
export { isPayVideoPlayerBlock } from './utils/payVideoPlayer.js';
export { isPayVideoListBlock } from './utils/payVideoList.js';
export { isPayVideoSeriesBlock } from './utils/payVideoSeries.js';

/** 是否为跳转链接组件 */
export function isQrcodeNavBlock(type) {
	const template = TEMPLATE_MAP[type];
	return template?.library === 'component' && template?.componentCategory === 'qrcodeNav';
}

const STYLE_CATEGORIES = ['title', 'body', 'divider', 'ungrouped'];
const COMPONENT_CATEGORIES = ['image', 'media', 'qrcodeNav', 'header'];
const PAY_COMPONENT_CATEGORIES = ['payGlobal', 'payVideo', 'payAudio', 'payImage', 'payFile', 'payLink'];

export const PAY_COMPONENT_CATEGORY_LABELS = {
	payGlobal: '全局支付',
	payVideo: '视频',
	payAudio: '音频',
	payImage: '图片',
	payFile: '文件',
	payLink: '付费跳转',
};

/** 左栏「支付组件」二级 Tab（与 PAY_COMPONENT_CATEGORIES 顺序一致） */
export function getPayComponentSubTabs() {
	return PAY_COMPONENT_CATEGORIES.map(key => ({
		key,
		label: PAY_COMPONENT_CATEGORY_LABELS[key] || key,
	}));
}

/** 样式库列表（按分类），新增区块只需在 TEMPLATE_MAP 注册 */
export function getStyleLibrary() {
	const library = STYLE_CATEGORIES.reduce((acc, key) => {
		acc[key] = [];
		return acc;
	}, {});

	Object.values(TEMPLATE_MAP).forEach(template => {
		if (template.library === 'component') return;
		const category = template.category || 'ungrouped';
		if (!library[category]) {
			library[category] = [];
		}
		library[category].push({
			id: `style-${template.type}`,
			type: template.type,
			label: template.label,
			vip: template.vip || false,
		});
	});

	return library;
}

/** 样式库「正文」分类中的第一个区块 type */
export function getFirstBodyBlockType() {
	const first = getStyleLibrary().body?.[0];
	return first?.type || 'body-basic';
}

/** 组件库列表（按分类），新增组件只需在 TEMPLATE_MAP 注册 library: 'component' */
export function getComponentLibrary() {
	const library = COMPONENT_CATEGORIES.reduce((acc, key) => {
		acc[key] = [];
		return acc;
	}, {});

	Object.values(TEMPLATE_MAP).forEach(template => {
		if (template.library !== 'component') return;
		if (template.hideFromLibrary) return;
		if (PAY_COMPONENT_CATEGORIES.includes(template.componentCategory)) return;
		const category = template.componentCategory || 'image';
		if (!library[category]) {
			library[category] = [];
		}
		library[category].push({
			id: `component-${template.type}`,
			type: template.type,
			name: template.label,
			icon: template.componentIcon || 'mbm-iconfont mbm-shipin',
			vip: template.vip || false,
		});
	});

	return library;
}

/** 支付组件库列表（按分类） */
export function getPayComponentLibrary() {
	const library = PAY_COMPONENT_CATEGORIES.reduce((acc, key) => {
		acc[key] = [];
		return acc;
	}, {});

	Object.values(TEMPLATE_MAP).forEach(template => {
		if (template.library !== 'component') return;
		const category = template.componentCategory;
		if (!PAY_COMPONENT_CATEGORIES.includes(category)) return;
		library[category].push({
			id: `component-${template.type}`,
			type: template.type,
			name: template.label,
			icon: template.componentIcon || 'mbm-iconfont mbm-shangpin',
			vip: template.vip || false,
		});
	});

	return library;
}

export function getStylePreviewData(type) {
	const template = TEMPLATE_MAP[type];
	if (!template) return {};
	if (template.previewData) {
		return template.previewData();
	}
	if (template.createData) {
		return template.createData();
	}
	return {};
}

export function createBlock(type, existingBlocks = []) {
	const template = TEMPLATE_MAP[type];
	if (!template) return null;

	const data = typeof template.createData === 'function'
		? template.createData()
		: (typeof template.previewData === 'function' ? template.previewData() : {});

	const baseId = getBlockTypeId(type);
	const idsBefore = existingBlocks.map(b => b.id);
	const id = makeUniqueBlockId(type, baseId, existingBlocks);

	return {
		id,
		uid: uuidv4(),
		type: template.type || type,
		data,
	};
}

/** 选中区块时右上角额外操作按钮（删除由外壳统一提供） */
export function getBlockToolbarActions(type) {
	const template = TEMPLATE_MAP[type];
	return template?.toolbarActions ? [...template.toolbarActions] : [];
}

export function duplicateBlock(block, existingBlocks = []) {
	if (!block) return null;

	// 复用 createBlock 的逻辑生成唯一 id（和左侧添加组件一致）
	const newBlock = createBlock(block.type, existingBlocks);
	if (!newBlock) return null;

	// 保留原始数据（深拷贝）
	newBlock.data = JSON.parse(JSON.stringify(block.data));

	return newBlock;
}

/**
 * 根据已有 blocks 中同 type 的数量生成唯一 id（后缀递增）
 * @param {string} type 组件类型
 * @param {string} baseId 固定 6 位 id
 * @param {Array} blocks 现有 block 列表
 * @param {string} [excludeUid] 复制时排除源 block 的 uid
 */
export function makeUniqueBlockId(type, baseId, blocks = [], excludeUid = '') {
	// 排除自身后，剩余同类型 blocks
	const same = blocks.filter(b => b.type === type && b.uid !== excludeUid);

	// 如果没有其他同类型 block（复制第一个组件）
	if (same.length === 0) {
		// 第一个该类型组件复制时，源 block 占用了 baseId，所以生成 baseId-1
		return `${baseId}-1`;
	}

	// 有其他同类型 block → 找最大后缀，生成 max+1
	let maxSuffix = 0;
	same.forEach((b) => {
		if (b.id === baseId) { maxSuffix = Math.max(maxSuffix, 0); return; }
		const match = String(b.id).match(new RegExp(`^${baseId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`));
		if (match) maxSuffix = Math.max(maxSuffix, parseInt(match[1], 10));
	});

	const result = `${baseId}-${maxSuffix + 1}`;
	return result;
}
